import { create } from 'zustand'

const hiddenKey = (accountId: string | null) =>
  accountId ? `ispot-nav-hidden-items-${accountId}` : 'ispot-nav-hidden-items'
const pinnedKey = (accountId: string | null) =>
  accountId ? `ispot-sidebar-pinned-${accountId}` : 'ispot-sidebar-pinned'
const orderKey = (accountId: string | null) =>
  accountId ? `ispot-nav-item-order-${accountId}` : 'ispot-nav-item-order'

interface NavVisibilityState {
  hiddenItems: string[]
  isPinned: boolean
  itemOrder: Record<string, string[]>
  setHiddenItems: (items: string[]) => void
  setItemOrder: (order: Record<string, string[]>) => void
  togglePinned: () => void
  setAccount: (accountId: string | null) => void
  _accountId: string | null
}

export const DEFAULT_HIDDEN_ITEMS = [
  'fit-thesis',
  'canvas-solution',
  'canvas',
  'smartdocs',
  'dual-space',
  'solution-configurator',
  'discovery',
  'discovery-assistant',
  'pdf',
  'crm',
  'reports',
]

function readHidden(accountId: string | null): string[] {
  try {
    const v = localStorage.getItem(hiddenKey(accountId))
    return v ? JSON.parse(v) : DEFAULT_HIDDEN_ITEMS
  } catch { return DEFAULT_HIDDEN_ITEMS }
}

function readPinned(accountId: string | null): boolean {
  try {
    const v = localStorage.getItem(pinnedKey(accountId))
    return v === null ? true : v === 'true'
  } catch { return true }
}

function readOrder(accountId: string | null): Record<string, string[]> {
  try {
    const v = localStorage.getItem(orderKey(accountId))
    return v ? JSON.parse(v) : {}
  } catch { return {} }
}

export const useNavVisibilityStore = create<NavVisibilityState>((set, get) => ({
  _accountId: null,
  hiddenItems: readHidden(null),
  isPinned: readPinned(null),
  itemOrder: readOrder(null),

  setAccount: (accountId) => {
    set({
      _accountId: accountId,
      hiddenItems: readHidden(accountId),
      isPinned: readPinned(accountId),
      itemOrder: readOrder(accountId),
    })
  },

  setHiddenItems: (items) => {
    const { _accountId } = get()
    try { localStorage.setItem(hiddenKey(_accountId), JSON.stringify(items)) } catch { /* ignore */ }
    set({ hiddenItems: items })
  },

  setItemOrder: (order) => {
    const { _accountId } = get()
    try { localStorage.setItem(orderKey(_accountId), JSON.stringify(order)) } catch { /* ignore */ }
    set({ itemOrder: order })
  },

  togglePinned: () => {
    const { _accountId } = get()
    const next = !get().isPinned
    try { localStorage.setItem(pinnedKey(_accountId), String(next)) } catch { /* ignore */ }
    set({ isPinned: next })
  },
}))
